import { useEffect } from 'react'
import {
  ColorInput,
  NumberInput,
  Popover,
  SegmentedControl,
  Select,
  Slider,
  Stack,
  Switch,
  Text,
  Textarea,
  TextInput,
} from '@mantine/core'
import { RichTextEditor, useRichTextEditorContext } from '@mantine/tiptap'
import { useEditor } from '@tiptap/react'
import StarterKit from '@tiptap/starter-kit'
import emojiData from '@emoji-mart/data'
import EmojiPicker from '@emoji-mart/react'
import { IconMoodSmile } from '@tabler/icons-react'
import { useTranslation } from 'react-i18next'
import { SuggestionChips } from '../components/forms/SuggestionChips'
import { ColorSuggestions } from '../components/forms/ColorSuggestions'
import { colorSuggestions, extractedColorSuggestions, fieldSuggestions } from '../utils/aiSuggestions'

const shared = (field, error) => ({
  label: field.label,
  description: field.help,
  error,
  size: 'sm',
})

// An alternative is plain text while a rich-text field holds markup, so the two
// are compared stripped. Only for deciding which chip is the current one; the
// field's own value is never rewritten here.
const plainText = (value) => (typeof value === 'string'
  ? value.replace(/<[^>]*>/g, '').trim()
  : '')

export function TextField({ field, value, onChange, error, configuration, path }) {
  const Component = field.type === 'textarea' ? Textarea : TextInput
  const inputType = ['url', 'email', 'tel', 'date', 'time'].includes(field.type)
    ? field.type
    : 'text'
  return (
    <Stack gap={2}>
      <Component
        {...shared(field, error)}
        type={inputType}
        value={value || ''}
        minRows={field.type === 'textarea' ? 2 : undefined}
        autosize={field.type === 'textarea' ? true : undefined}
        onChange={(event) => onChange(event.currentTarget.value)}
      />
      <SuggestionChips
        options={fieldSuggestions(configuration, field, path)}
        value={value || ''}
        onSelect={onChange}
      />
    </Stack>
  )
}

export function SelectField({ field, value, onChange, error, options }) {
  return (
    <Select
      {...shared(field, error)}
      data={(options || field.options || []).map((option) => ({
        value: option.value,
        label: option.label,
      }))}
      value={value || null}
      allowDeselect={false}
      searchable={(options || field.options || []).length > 8}
      onChange={onChange}
    />
  )
}

export function SegmentedField({ field, value, onChange, options }) {
  return (
    <Stack gap={5}>
      <Text size="sm" fw={500}>{field.label}</Text>
      <SegmentedControl
        fullWidth
        size="xs"
        data={(options || field.options || []).map((option) => ({
          value: option.value,
          label: option.label,
        }))}
        value={value}
        onChange={onChange}
      />
    </Stack>
  )
}

export function BooleanField({ field, value, onChange, disabled }) {
  return (
    <Switch
      label={field.label}
      description={field.help}
      checked={Boolean(value)}
      disabled={disabled}
      onChange={(event) => onChange(event.currentTarget.checked)}
    />
  )
}

export function SliderField({ field, value, onChange, error }) {
  const rules = field.validation || {}
  const suffix = '%'
  return (
    <Stack gap={5}>
      <Text size="sm" fw={500}>{field.label}: {value}{suffix}</Text>
      <Slider
        aria-label={field.label}
        min={rules.min} max={rules.max} step={rules.step || 1}
        value={Number(value)} onChange={onChange}
        label={(next) => `${next}${suffix}`}
        thumbLabel={`${field.label}: ${value}${suffix}`}
      />
      {field.help && <Text size="xs" c="dimmed">{field.help}</Text>}
      {error && <Text size="xs" c="red">{error}</Text>}
    </Stack>
  )
}

// Shown after the run's own palette: a house colour is a fallback for an
// advertiser we know nothing about, not a suggestion for this one.
const HOUSE_SWATCHES = ['#d00b65', '#f12983', '#3c64a2', '#222222', '#ffffff', '#f2efe9']

export function ColorField({ field, value, onChange, error, configuration }) {
  const offered = colorSuggestions(configuration)
  const swatches = [
    ...offered,
    ...HOUSE_SWATCHES.filter((color) => !offered.includes(color)),
  ]
  return (
    <Stack gap={0}>
      <ColorInput
        {...shared(field, error)}
        value={value || '#000000'}
        format="hex"
        swatches={swatches}
        swatchesPerRow={Math.min(swatches.length, 8) || 1}
        onChange={onChange}
      />
      <ColorSuggestions
        options={extractedColorSuggestions(configuration)}
        value={value}
        onSelect={onChange}
      />
    </Stack>
  )
}

export function NumberField({ field, value, onChange, error }) {
  const rules = field.validation || {}
  return (
    <NumberInput
      {...shared(field, error)}
      value={Number(value || 0)}
      min={rules.min}
      max={rules.max}
      step={field.type === 'duration' ? 100 : (field.validation?.step || 1)}
      suffix={field.type === 'duration' ? ' ms' : undefined}
      onChange={(nextValue) => onChange(Number(nextValue || 0))}
    />
  )
}

export function DateTimeField({ field, value, onChange, error }) {
  let localValue = ''
  if (value) {
    const date = new Date(value)
    if (!Number.isNaN(date.getTime())) {
      localValue = new Date(date.getTime() - date.getTimezoneOffset() * 60000)
        .toISOString()
        .slice(0, 16)
    }
  }

  return (
    <TextInput
      {...shared(field, error)}
      type="datetime-local"
      value={localValue}
      onChange={(event) => {
        const next = event.currentTarget.value
        onChange(next ? new Date(next).toISOString() : null)
      }}
    />
  )
}

function InsertEmojiControl() {
  const { t } = useTranslation()
  const { editor } = useRichTextEditorContext()

  return (
    <Popover width={350} position="bottom-start" withArrow shadow="md">
      <Popover.Target>
        <RichTextEditor.Control aria-label={t('editorV2.form.common.insertEmoji')} title={t('editorV2.form.common.insertEmoji')}>
          <IconMoodSmile stroke={1.5} size={16} />
        </RichTextEditor.Control>
      </Popover.Target>
      <Popover.Dropdown p={0}>
        <EmojiPicker
          data={emojiData}
          onEmojiSelect={({ native }) => editor?.commands.insertContent(native)}
        />
      </Popover.Dropdown>
    </Popover>
  )
}

export function RichTextField({ field, value, onChange, error, configuration, path }) {
  const editor = useEditor({
    extensions: [
      // Lists, headings and blockquotes stay off deliberately: the template runtime's
      // sanitizer (ALLOWED_HEADLINE_TAGS) keeps only STRONG/B/EM/I/U/S/BR/SPAN, so any
      // block-level markup would look right in the editor and vanish from the ad.
      StarterKit.configure({
        heading: false,
        bulletList: false,
        orderedList: false,
        blockquote: false,
        codeBlock: false,
        horizontalRule: false,
      }),
    ],
    content: value || '',
    onUpdate: ({ editor: currentEditor }) => {
      onChange(currentEditor.getHTML().replace(/<\/?p>/g, '').trim())
    },
  })

  useEffect(() => {
    if (!editor) return
    const currentValue = editor.getHTML().replace(/<\/?p>/g, '').trim()
    if (currentValue !== value) {
      editor.commands.setContent(value || '', { emitUpdate: false })
    }
  }, [editor, value])

  return (
    <Stack gap={5}>
      <Text size="sm" fw={500}>{field.label}</Text>
      <RichTextEditor editor={editor}>
        <RichTextEditor.Toolbar sticky={false}>
          <RichTextEditor.ControlsGroup>
            <RichTextEditor.Bold />
            <RichTextEditor.Italic />
            <RichTextEditor.Underline />
            <RichTextEditor.Strikethrough />
            <RichTextEditor.ClearFormatting />
          </RichTextEditor.ControlsGroup>
          <RichTextEditor.ControlsGroup>
            <InsertEmojiControl />
          </RichTextEditor.ControlsGroup>
          <RichTextEditor.ControlsGroup>
            <RichTextEditor.Undo />
            <RichTextEditor.Redo />
          </RichTextEditor.ControlsGroup>
        </RichTextEditor.Toolbar>
        <RichTextEditor.Content mih={76} />
      </RichTextEditor>
      <SuggestionChips
        options={fieldSuggestions(configuration, field, path)}
        value={plainText(value)}
        onSelect={onChange}
      />
      {field.help && <Text size="xs" c="dimmed">{field.help}</Text>}
      {error && <Text size="xs" c="red">{error}</Text>}
    </Stack>
  )
}

export const basicFieldRegistry = {
  text: TextField,
  textarea: TextField,
  url: TextField,
  email: TextField,
  tel: TextField,
  date: TextField,
  time: TextField,
  select: SelectField,
  segmented: SegmentedField,
  boolean: BooleanField,
  color: ColorField,
  integer: NumberField,
  number: NumberField,
  range: NumberField,
  duration: NumberField,
  datetime: DateTimeField,
  'rich-text': RichTextField,
}
